import { Building2, Edit3, Save, Trash2, Upload, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { httpService } from "../services/httpService";
import { Provider, ProviderData, ProviderTier } from "../types/provider";
import ConfirmationModal from "./ConfirmationModal";
import ProviderTierSelector from "./admin/ProviderTierSelector";

interface ProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider?: Provider | null;
  mode: "create" | "edit";
  onSave: (providerData: Partial<Provider>) => Promise<Provider | void>;
  onDelete?: (providerId: string) => Promise<void>;
}

const ProviderModal: React.FC<ProviderModalProps> = ({ isOpen, onClose, provider, mode, onSave, onDelete }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDarkMode = theme === "dark";
  
  // Check if user has admin role
  const userRoles = user?.userRoles?.map(ur => ur.role) || [];
  const hasRole = (role: string) => userRoles.includes(role);
  const isAdmin = hasRole("ADMIN");
  
  // Don't render tabs until user data is loaded
  const isUserLoaded = !!user;
  
  const [formData, setFormData] = useState({
    name: "",
  });
  // Provider `data.tier` is edited separately from name (PWA-2335). null = no tier.
  const [tier, setTier] = useState<ProviderTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [autoCreatedProviderId, setAutoCreatedProviderId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && provider) {
        setFormData({
          name: provider.name,
        });
        setTier(provider.data?.tier ?? null);
        // Explicitly fetch latest logo to avoid showing a stale image
        (async () => {
          try {
            // Clear any previous image immediately and show spinner
            setLogoFile(null);
            setLogoPreview(null);
            setLogoUrl(null);
            setLogoUploadSuccess(false);
            setUploadingLogo(true);

            // Call the logo endpoint to get a fresh URL (handles 404 gracefully)
            const res = await httpService.get(`/providers/${provider.id}/logo`);
            if (res?.data?.logoUrl) {
              const url: string = res.data.logoUrl;
              const sep = url.includes('?') ? '&' : '?';
              setLogoUrl(`${url}${sep}v=${Date.now()}`);
            }
          } catch (e) {
            // Ignore if no logo; keep it empty
            setLogoUrl(null);
          } finally {
            setUploadingLogo(false);
          }
        })();
      } else {
        setFormData({
          name: "",
        });
        setTier(null);
        // Reset logo state for new provider
        setLogoFile(null);
        setLogoPreview(null);
        setLogoUrl(null);
        setLogoUploadSuccess(false);
      }
      setError(null);
    }
  }, [isOpen, mode, provider]);

  // Handle logo file selection
  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB');
        return;
      }

      setLogoFile(file);
      setError(null);
      setLogoUploadSuccess(false);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Start upload flow immediately
      if (mode === 'edit' && provider) {
        // Show spinner immediately
        setUploadingLogo(true);
        uploadLogo(provider.id, file);
      } else if (mode === 'create') {
        if (!formData.name.trim()) {
          setError('Please enter provider name before uploading a logo');
          return;
        }
        // Auto create provider, then upload
        (async () => {
          try {
            setUploadingLogo(true);
            const savedProvider = await onSave(buildPayload());
            if (savedProvider && typeof savedProvider === 'object' && 'id' in savedProvider) {
              const newId = (savedProvider as any).id as string;
              setAutoCreatedProviderId(newId);
              await uploadLogo(newId, file);
            } else {
              setError('Failed to create provider before uploading logo');
              setUploadingLogo(false);
            }
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to create provider');
            setUploadingLogo(false);
          }
        })();
      }
    }
  };

  // Upload logo
  const uploadLogo = async (providerId: string, file?: File) => {
    const fileToUpload = file || logoFile;
    if (!fileToUpload) return;

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', fileToUpload);

      const response = await httpService.post(`/providers/${providerId}/logo`, formData, {
        timeout: 60000 // 60 seconds for file uploads
      });

      // Clear the file selection
      setLogoFile(null);
      setLogoPreview(null);

      // Use the publicUrl from POST response and cache-bust
      if (response.data.logo?.publicUrl) {
        const url = response.data.logo.publicUrl;
        const sep = url.includes('?') ? '&' : '?';
        setLogoUrl(`${url}${sep}v=${Date.now()}`);
        setLogoUploadSuccess(true);
        setTimeout(() => setLogoUploadSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setError('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Delete logo
  const deleteLogo = async (providerId: string) => {
    try {
      await httpService.delete(`/providers/${providerId}/logo`);
      setLogoUrl(null);
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      console.error('Failed to delete logo:', error);
      setError('Failed to delete logo. Please try again.');
    }
  };

  // Build the save payload with explicit `data` semantics (PWA-2335). The
  // backend writes the full `data` value, so we must preserve any non-tier keys
  // that already exist on the provider rather than overwrite the whole blob:
  //  - tier unchanged                -> omit `data` (leave column untouched)
  //  - tier set/changed             -> merge { ...existingData, tier }
  //  - tier cleared, other keys left -> write existing data minus `tier`
  //  - tier cleared, nothing left    -> null (clear the column)
  const buildPayload = (): Partial<Provider> => {
    const payload: Partial<Provider> = { name: formData.name };
    const originalTier = provider?.data?.tier ?? null;
    if (tier !== originalTier) {
      if (tier) {
        payload.data = { ...(provider?.data ?? {}), tier };
      } else {
        const rest: ProviderData = { ...(provider?.data ?? {}) };
        delete rest.tier;
        payload.data = Object.keys(rest).length > 0 ? rest : null;
      }
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // If we already auto-created the provider during logo upload in create mode, just close
      if (mode === 'create' && autoCreatedProviderId) {
        onClose();
        return;
      }
      const savedProvider = await onSave(buildPayload());
      
      // Upload logo if a new file was selected
      if (logoFile) {
        if (mode === "create" && savedProvider && typeof savedProvider === 'object' && 'id' in savedProvider) {
          // For new providers, use the returned provider ID
          await uploadLogo(savedProvider.id as string);
        } else if (mode === "edit" && provider) {
          // For existing providers, use the current provider ID
          await uploadLogo(provider.id);
        }
        
        // Don't close modal after logo upload - stay on details tab
        // User can manually close or switch to other tabs
      } else {
        // Only close modal if no logo upload
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!provider || !onDelete) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!provider || !onDelete) return;

    setLoading(true);
    try {
      await onDelete(provider.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl ${
          mode === "edit" && isUserLoaded && isAdmin ? "max-w-6xl w-full" : "max-w-md w-full"
        } max-h-[90vh] overflow-y-auto`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="flex items-center">
            {mode === "create" ? (
              <Building2 className={`w-6 h-6 mr-3 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            ) : (
              <Edit3 className={`w-6 h-6 mr-3 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
            )}
            <h2 className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {mode === "create" ? "Create New Provider" : "Edit Provider"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className={`rounded-lg p-3 ${
                    isDarkMode ? "bg-red-900/30 border border-red-700" : "bg-red-50 border border-red-200"
                  }`}
                >
                  <p className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-600"}`}>{error}</p>
                </div>
              )}

              {logoUploadSuccess && (
                <div
                  className={`rounded-lg p-3 ${
                    isDarkMode ? "bg-green-900/30 border border-green-700" : "bg-green-50 border border-green-200"
                  }`}
                >
                  <p className={`text-sm ${isDarkMode ? "text-green-300" : "text-green-600"}`}>
                    ✅ Logo uploaded successfully!
                  </p>
                </div>
              )}

              {/* Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Provider Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-300"
                  }`}
                  placeholder="Investment Provider Name"
                />
              </div>

              {/* Tier */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Tier
                </label>
                <ProviderTierSelector value={tier} onChange={setTier} />
              </div>

              {/* Logo Upload */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Provider Logo
                </label>
                
                {/* Current Logo Display */}
                {(logoUrl || logoPreview || uploadingLogo) && (
                  <div className="mb-3">
                    <div className="flex items-center space-x-3">
                      {uploadingLogo ? (
                        <div className="w-16 h-16 bg-gray-200 rounded-lg border flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                      ) : (
                        <img
                          src={logoPreview || logoUrl || ''}
                          alt="Provider logo"
                          className="w-16 h-16 object-cover rounded-lg border"
                        />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                          {uploadingLogo ? "Uploading logo..." : (logoPreview ? "New logo selected" : "Current logo")}
                        </p>
                        {mode === "edit" && provider && !logoPreview && !uploadingLogo && (
                          <button
                            type="button"
                            onClick={() => deleteLogo(provider.id)}
                            className={`text-sm ${isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-700"}`}
                          >
                            Remove logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}


                {/* File Input */}
                <div className="flex items-center space-x-3">
                  <label
                    className={`flex items-center px-4 py-2 border border-dashed rounded-lg cursor-pointer transition-colors ${
                      isDarkMode
                        ? "border-gray-600 hover:border-gray-500 hover:bg-gray-700/50"
                        : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      {logoUrl || logoPreview ? "Change Logo" : "Upload Logo"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  
                  {logoFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className={`text-sm ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-700"}`}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                
                <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                  Supported formats: PNG, JPG, JPEG, GIF, WebP. Max size: 10MB.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4">
                <div>
                  {mode === "edit" && onDelete && (
                    <button
                      type="button"
                      onClick={handleDeleteClick}
                      disabled={loading}
                      className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                        isDarkMode
                          ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          : "text-red-600 hover:text-red-700 hover:bg-red-50"
                      }`}
                    >
                      <Trash2 className="w-4 h-4 inline mr-2" />
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                      isDarkMode
                        ? "text-gray-300 hover:text-white hover:bg-gray-700"
                        : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {loading && logoFile ? "Uploading Logo..." : (mode === "create" ? "Create Provider" : "Save Changes")}
                  </button>
                </div>
              </div>
            </form>
        </div>

        {/* Delete Provider Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          title="Delete Provider"
          message={`Are you sure you want to delete provider "${provider?.name}"?`}
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ProviderModal;
